import React from 'react'
import type { CardWriteHandler } from '../../types/cardWrite'
import { Box } from '@mui/material'
import { DashboardLayout, DashboardCell, DashboardConfig } from '../../types/gotypes'
import DashboardRowComponent from './DashboardRowComponent'

interface DashboardLayoutComponentProps {
  layout: DashboardLayout
  customer?: string
  job?: string
  dashboardId?: string
  readOnly?: boolean
  /** #1219 gate 2: may this viewer spend tokens regenerating a Gen AI card on
   *  this dashboard — `canOverwriteDashboard(...)`, computed once by the page
   *  and threaded down here. Undefined/absent means false: a surface that
   *  never resolves this (a card-write-only view, a preview) must not enable
   *  regeneration by omission. */
  canRegenerate?: boolean
  globalFilter?: string
  jobFilters?: Record<string, string>
  colorMap?: Record<string, string>
  /** Theme id → flattened colour map (#1193) — resolves chart-level brand_palette_key. */
  themeColorMaps?: Record<string, Record<string, string>>
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
  // Returns a promise on the surfaces that persist the change, so a caller can
  // await the save and react to a rejected write (#963).
  onCellUpdate?: (rowIndex: number, cellIndex: number, cell: DashboardCell) => void | Promise<void>
  /** #1256: narrow card text/sign-off write for any logged-in user; passed straight through. */
  onCardWrite?: CardWriteHandler
  /**
   * Widget address prefix (#969 Part F — dashboardWidgetWalk.ts's path
   * scheme). Set by a parent DashboardCellComponent rendering a nested
   * "layout" cell, so its inner widgets continue the same address chain.
   * Undefined at the top-level call — there the address starts fresh from
   * `currentPageIndex`, matching DashboardRowComponent's existing
   * `row-${currentPageIndex}-${rowIndex}` DOM id.
   */
  pathPrefix?: string
  /** Review mode (#969 Part F) — per-widget dot indicators render only here. */
  reviewMode?: boolean
  /** Widget paths with an unreviewed change, for the dot indicator. */
  changedWidgetPaths?: Set<string>
}

const DashboardLayoutComponent: React.FC<DashboardLayoutComponentProps> = ({
  layout,
  customer,
  job,
  dashboardId,
  readOnly,
  canRegenerate,
  globalFilter,
  jobFilters,
  colorMap,
  themeColorMaps,
  dashboardConfig,
  currentPageIndex = 0,
  onCellUpdate,
  onCardWrite,
  pathPrefix,
  reviewMode,
  changedWidgetPaths,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {layout.rows.map((row, index) => (
        <DashboardRowComponent
          key={index}
          row={row}
          customer={customer}
          job={job}
          dashboardId={dashboardId}
          readOnly={readOnly}
          canRegenerate={canRegenerate}
          globalFilter={globalFilter}
          jobFilters={jobFilters}
          colorMap={colorMap}
          themeColorMaps={themeColorMaps}
          dashboardConfig={dashboardConfig}
          currentPageIndex={currentPageIndex}
          rowIndex={index}
          pathPrefix={pathPrefix}
          reviewMode={reviewMode}
          changedWidgetPaths={changedWidgetPaths}
          onCellUpdate={onCellUpdate ? (cellIndex, cell) => onCellUpdate(index, cellIndex, cell) : undefined}
          onCardWrite={onCardWrite}
        />
      ))}
    </Box>
  )
}

export default DashboardLayoutComponent
